import { HttpContextToken, HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Inject, Injectable, Injector } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { Observable, catchError, finalize, map, shareReplay, switchMap, throwError } from 'rxjs';
import { environment } from '../../../environment/environment';
import { API_BASE_URL } from '../services/api/api-base-url.token';
import { AuthService } from '../services/auth.service';

/**
 * Per-request flag set when the interceptor has already refreshed and replayed
 * a request once. Stops the retry loop from re-firing on a sustained 401.
 */
export const AUTH_REFRESH_RETRIED = new HttpContextToken<boolean>(() => false);

/**
 * Reactive recovery layer on top of MsalInterceptor.
 *
 * MsalInterceptor attaches a cached access token and falls back to
 * `acquireTokenRedirect` when silent acquisition fails. It does NOT react
 * to a 401 returned by the API after the token was attached (Conditional
 * Access drift, audience mismatch, server-side revocation, clock skew).
 *
 * This interceptor closes that gap: on 401 against a protected URL, it forces
 * a silent refresh once, replays the request with the fresh Bearer, and -
 * if the silent refresh itself surfaces InteractionRequiredAuthError - sends
 * the user through Entra to renew the session on the same page.
 *
 * Singleton refresh via `shareReplay(1)` + `finalize` deduplicates concurrent
 * 401s into a single MSAL call (a burst of 10 parallel requests doing 401
 * triggers one `acquireTokenSilent`, not ten).
 */
@Injectable()
export class AuthRefreshInterceptor implements HttpInterceptor {
  private refresh$: Observable<string | null> | null = null;

  // AuthService is resolved lazily through the Injector to break the
  // HTTP_INTERCEPTORS → AuthService → UserStore → HttpClient → HTTP_INTERCEPTORS
  // dependency cycle that Angular's DI rejects with NG0200 at boot.
  constructor(
    private readonly msalService: MsalService,
    private readonly injector: Injector,
    @Inject(API_BASE_URL) private readonly apiBaseUrl: string,
  ) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError((error: unknown) => {
        if (!this.shouldRecover(error, request)) {
          return throwError(() => error);
        }
        return this.getFreshToken().pipe(
          switchMap(token => {
            if (!token) {
              return throwError(() => error);
            }
            const replay = request.clone({
              setHeaders: { Authorization: `Bearer ${token}` },
              context: request.context.set(AUTH_REFRESH_RETRIED, true),
            });
            return next.handle(replay);
          }),
          catchError(refreshError => this.handleRefreshFailure(refreshError, error)),
        );
      }),
    );
  }

  private shouldRecover(error: unknown, request: HttpRequest<unknown>): boolean {
    if (!(error instanceof HttpErrorResponse) || error.status !== 401) return false;
    if (request.context.get(AUTH_REFRESH_RETRIED)) return false;
    return this.isProtectedUrl(request.url);
  }

  private isProtectedUrl(url: string): boolean {
    if (!this.apiBaseUrl) return false;
    // The api-base-url interceptor has already prefixed relative URLs by the
    // time we see them, so an absolute match is sufficient. Skip MSAL's own
    // token endpoints (login.microsoftonline.com) which never go through us.
    return url.startsWith(this.apiBaseUrl);
  }

  private getFreshToken(): Observable<string | null> {
    if (this.refresh$) return this.refresh$;
    const account = this.msalService.instance.getActiveAccount();
    if (!account) {
      return throwError(() => new InteractionRequiredAuthError('no_account', 'No active MSAL account'));
    }
    this.refresh$ = this.msalService
      .acquireTokenSilent({
        scopes: environment.MSAL.scopes,
        account,
        forceRefresh: true,
      })
      .pipe(
        map(result => result?.accessToken ?? null),
        shareReplay(1),
        finalize(() => {
          this.refresh$ = null;
        }),
      );
    return this.refresh$;
  }

  private handleRefreshFailure(refreshError: unknown, originalError: unknown): Observable<never> {
    if (refreshError instanceof InteractionRequiredAuthError) {
      // MSAL needs the user at Entra. A redirect keeps Entra's sign-in cookie,
      // so the user usually returns without typing anything; a logout would
      // end it and force a full sign-in with MFA.
      this.injector.get(AuthService).reauthenticate();
    }
    return throwError(() => originalError);
  }
}
